import { Injectable } from '@angular/core';
import {
    AddCartItemDto,
    BookingConfiguredData,
    CartDto,
    ProductBookingData,
} from './booking-config.model';
import { Observable } from 'rxjs';
import { getBookingUrl } from 'src/app/utilities/url.utils';
import { NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import * as dayjs from 'dayjs';
import {
    addDaysToDateStruct,
    createDateStringReverse,
    getTodayNgb,
} from 'src/app/controls/calendar/calendar.utils';
import { DealDto } from 'src/app/model/deal.model';
import { UserService } from 'src/app/services/user.service';
import { AuthHttpClientNoXService } from 'src/app/services/auth-http-client-noX.service';
import { CurrencyLanguageService } from '@app/shared/currency-language.service';
import { CheckoutService } from 'src/app/user/checkout/checkout.service';
import { createDealInfo } from './booking-config.utils';

@Injectable({
    providedIn: 'root',
})
export class BookingService {
    isUserLoggedIn = false;

    constructor(
        private userService: UserService,
        private authHttpClient: AuthHttpClientNoXService,
        private currencyLanguageService: CurrencyLanguageService,
        private checkoutService: CheckoutService
    ) {}

    get locale() {
        return this.currencyLanguageService.getLocaleDataImmediate();
    }

    addToCartV2(
        bookingConfiguredData: BookingConfiguredData,
        productDetails: DealDto,
        currencyId: 1 | 2
    ): Observable<CartDto> {
        const apiUrl = this.userService.isLoggedIn
            ? `${getBookingUrl()}carts/items`
            : `${getBookingUrl()}carts/items/as-guest`;
        const localCart = this.userService.getLocalCart();
        // if there is a user cart use that, else use the local cart
        const cartId = localCart?.userCartId;
        const { checkinDate, checkoutDate, expiryDate } = this.assembleDates(
            bookingConfiguredData,
            productDetails
        );
        const {
            productBookingData,
            totalPrice,
            totalFullPrice,
            rewardPointsEarned,
        } = bookingConfiguredData;

        const postBody: AddCartItemDto = {
            cartId,
            outletId: productDetails.outletId,
            dealId: productBookingData?.id,
            dealCategoryId: productDetails.categoryId,
            sectionId: productDetails.categoryId,
            dealTitle: productDetails.dealTitle,
            dealSubtitle: productDetails.subTitle,
            rewardPointsEarned,
            quantity: 1,
            subtotal: totalPrice,
            purchaseCurrencyId: currencyId,
            grandTotal: totalFullPrice,
            dealInfo: createDealInfo(productBookingData, productDetails),
        };

        checkinDate &&
            (postBody.checkIn = createDateStringReverse(checkinDate));
        checkoutDate &&
            (postBody.checkOut = createDateStringReverse(checkoutDate));
        expiryDate &&
            (postBody.expiryDate = createDateStringReverse(expiryDate));

        const queryParams = new URLSearchParams({
            orgId: '1',
            countryCode: this.locale.country,
            customerGroupId: this.locale.customerGroupId?.toString?.(),
        });

        return this.authHttpClient.post<CartDto, AddCartItemDto>(
            `${apiUrl}?${queryParams.toString()}`,
            postBody
        );
    }

    private assembleDates(
        bookingConfiguredData: BookingConfiguredData,
        productDetails: DealDto
    ): {
        checkinDate: NgbDateStruct;
        checkoutDate: NgbDateStruct;
        expiryDate: NgbDateStruct;
    } {
        const { checkinDate, checkoutDate, selectedDate } =
            bookingConfiguredData.productBookingData;
        const dealExpiryDate = this.computeExpirationDate(productDetails);

        if (checkinDate && checkoutDate) {
            return {
                expiryDate: dealExpiryDate,
                checkinDate,
                checkoutDate,
            };
        }

        if (selectedDate) {
            return {
                expiryDate: dealExpiryDate,
                checkinDate: selectedDate,
                checkoutDate: selectedDate,
            };
        }

        return {
            expiryDate: dealExpiryDate,
            checkinDate: undefined,
            checkoutDate: undefined,
        };
    }

    private computeExpirationDate(productDetails: DealDto): NgbDateStruct {
        const today = getTodayNgb();
        if (productDetails.validityDays) {
            return addDaysToDateStruct(today, productDetails.validityDays);
        }
        if (productDetails.expirationDate) {
            const expirationdatedayjs = dayjs(productDetails.expirationDate);
            const expirationDateNgb = new NgbDate(
                expirationdatedayjs.year(),
                expirationdatedayjs.month() + 1,
                expirationdatedayjs.date()
            );
            if (expirationDateNgb.after(today)) {
                return expirationDateNgb;
            }
        }

        return undefined;
    }

    private createAddonDataDto(productData: ProductBookingData) {
        return productData.addOnConfigurations
            ? productData.addOnConfigurations.map((addonConfig) => {
                  return {
                      id: addonConfig.addonItem.id,
                      price_type: addonConfig.addonItem.priceType,
                      sku: addonConfig.addonItem.sku,
                      title: addonConfig.addonItem.title,
                      qty: addonConfig.quantity,
                  };
              })
            : '';
    }

    notifyCartUpdated() {
        this.checkoutService.notifyCartUpdated();
    }
}
